import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, X, ArrowRight, Car } from "lucide-react";
import { transferBooking, fetchVehicles } from "@/lib/api";

const CAPACITY_MSG = "Re-enter the total passengers as it exceeds the capacity of the new vehicle.";

export const TransferBookingDialog = ({ booking, onClose, onDone }) => {
  const [vehicles, setVehicles] = useState([]);
  const [newVehicle, setNewVehicle] = useState("");
  const [passengers, setPassengers] = useState(String(booking.passengers ?? 1));
  const [needPassenger, setNeedPassenger] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVehicles()
      .then((vs) => setVehicles(vs.filter((v) => v.name !== booking.vehicle)))
      .catch(() => {});
  }, [booking.vehicle]);

  const selected = useMemo(() => vehicles.find((v) => v.name === newVehicle) || null, [vehicles, newVehicle]);

  const onSelectVehicle = (name) => {
    setNewVehicle(name);
    setConfirmStep(false);
    const v = vehicles.find((x) => x.name === name);
    if (v && Number(booking.passengers) > v.capacity) {
      setNeedPassenger(true);
      setPassengers(String(v.capacity));
    } else {
      setNeedPassenger(false);
      setPassengers(String(booking.passengers ?? 1));
    }
  };

  const paxNum = Number(passengers);
  const paxValid = /^\d+$/.test(String(passengers).trim()) && paxNum >= 1 && (!selected || paxNum <= selected.capacity);
  const canProceed = !!newVehicle && paxValid;

  const doTransfer = async () => {
    setSubmitting(true);
    try {
      await transferBooking(booking.booking_ref, { new_vehicle: newVehicle, passengers: paxNum });
      toast.success(`Booking ${booking.booking_ref} transferred to ${newVehicle}`);
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not transfer booking";
      if (detail === CAPACITY_MSG) {
        setNeedPassenger(true);
        setConfirmStep(false);
      }
      toast.error(detail);
      // Status-based blocks are terminal — close so the admin refreshes state.
      if (detail.startsWith("Transfer blocked: Only Upcoming or In Use")) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition-all focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";
  const labelCls = "mb-1 block text-xs font-semibold text-[#0F172A]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="transfer-dialog">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#0F172A]">Transfer {booking.booking_ref}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="transfer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Current vehicle summary */}
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
          <Car className="h-4 w-4 shrink-0 text-[#5C2D91]" />
          <span className="font-semibold text-[#0F172A]" data-testid="transfer-current-vehicle">{booking.vehicle}</span>
          <span className="text-[#64748B]">&middot; {booking.passengers} pax &middot; {booking.employee_name}</span>
        </div>

        {!confirmStep ? (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Transfer to vehicle</label>
              <select
                value={newVehicle}
                onChange={(e) => onSelectVehicle(e.target.value)}
                className={inputCls}
                data-testid="transfer-vehicle-select"
              >
                <option value="">Select a vehicle…</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.name}>{v.name} (seats {v.capacity})</option>
                ))}
              </select>
            </div>

            {needPassenger && (
              <div>
                <label className={labelCls}>Total passengers (including the driver)</label>
                <input
                  type="number"
                  min={1}
                  max={selected?.capacity || undefined}
                  step={1}
                  value={passengers}
                  onChange={(e) => setPassengers(e.target.value)}
                  className={inputCls}
                  data-testid="transfer-passengers-input"
                />
                <p className="mt-1 text-[12px] font-medium text-amber-600" data-testid="transfer-capacity-msg">
                  {CAPACITY_MSG}
                  {selected && <span className="text-[#64748B]"> {selected.name} seats {selected.capacity}.</span>}
                </p>
              </div>
            )}

            <div className="mt-2 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50"
                data-testid="transfer-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => setConfirmStep(true)}
                disabled={!canProceed}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-50"
                data-testid="transfer-proceed"
              >
                Transfer <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="transfer-confirm-step">
            <div className="flex items-center justify-center gap-3 rounded-lg bg-slate-50 px-3 py-4 text-sm">
              <span className="font-semibold text-[#0F172A]">{booking.vehicle}</span>
              <ArrowRight className="h-4 w-4 text-[#5C2D91]" />
              <span className="font-semibold text-[#5C2D91]">{newVehicle}</span>
            </div>
            <p className="text-center text-sm text-[#334155]">
              Are you sure you want to transfer this booking from <span className="font-semibold text-[#0F172A]">{booking.vehicle}</span> to <span className="font-semibold text-[#5C2D91]">{newVehicle}</span>?
              {paxNum !== Number(booking.passengers) && (
                <span className="mt-1 block text-xs text-[#64748B]">Passengers: {booking.passengers} → {paxNum}</span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmStep(false)}
                disabled={submitting}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50 disabled:opacity-60"
                data-testid="transfer-confirm-back"
              >
                Back
              </button>
              <button
                onClick={doTransfer}
                disabled={submitting}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] active:scale-[0.98] disabled:opacity-60"
                data-testid="transfer-confirm"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Transferring…</> : "Confirm transfer"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
